/**
 * SpiderManTheme — visual + audio overlay that activates when the
 * user picks the "Spider-Man" theme in Settings. Renders subtle web
 * corners app-wide + swinging silhouette animations on the landing
 * dashboards, plus three easter eggs (see App.js wire-up).
 *
 * Rendered as a fixed-position overlay pinned at z-index 5 (above
 * the page canvas, below modals/toasts). Everything is
 * pointer-events-none so it never blocks clicks — except the
 * easter-egg hit-target on the email capture tile which is a
 * transparent button.
 *
 * Auto-detects "Miles Morales" vs "Peter Parker" mode on mount so
 * different sessions get different heroes (per user ask 2026-02).
 *
 * Perf: pure CSS animations, no rAF/requestAnimationFrame loops,
 * no external assets — no FPS impact on the manager dashboard
 * during checkpoint entry.
 */
import { useEffect, useMemo, useState } from "react";
import { playThwip, playWhoosh, playPow, unlock as unlockAudio } from "../lib/spiderSounds";

// Pick a hero randomly each session so managers see variety across
// visits. `peter` = red+blue, `miles` = red+black w/ electric accents.
function pickHero() {
  return Math.random() < 0.5 ? "peter" : "miles";
}

// Two web-corner SVGs (top-left + top-right variants). Kept
// inline so the theme has zero network dependencies.
const WEB_CORNER = ({ flip = false, color = "#ffffff", opacity = 0.35 }) => (
  <svg
    viewBox="0 0 120 120"
    className="w-32 h-32 sm:w-44 sm:h-44"
    style={{ transform: flip ? "scaleX(-1)" : "none", opacity }}
    aria-hidden="true"
  >
    <g stroke={color} strokeWidth="1.2" fill="none" strokeLinecap="round">
      {/* Radial strands */}
      {Array.from({ length: 8 }).map((_, i) => {
        const angle = (i * Math.PI) / 14; // fan out from top-left corner
        const x2 = Math.cos(angle) * 120;
        const y2 = Math.sin(angle) * 120;
        return <line key={`r${i}`} x1="0" y1="0" x2={x2} y2={y2} />;
      })}
      {/* Concentric web rings — arc paths approximating a curve */}
      {[24, 48, 72, 96].map((r, i) => (
        <path
          key={`c${i}`}
          d={`M ${r} 0 Q ${r * 0.85} ${r * 0.85} 0 ${r}`}
          strokeWidth={i === 0 ? 1.4 : 1}
        />
      ))}
    </g>
  </svg>
);

// Full-viewport swinging hero silhouette. Uses CSS keyframes defined
// in the <style> block below. Two flavors: peter (classic red+blue)
// and miles (red+black w/ electric accents). Redrawn 2026-02 with
// proper iconic proportions: oversized teardrop eye lenses with
// bold black outlines, muscular dynamic pose, black spider emblem
// on the chest, and a fine web-line grid — the details that make
// Spider-Man read as Spider-Man and not "generic red hero".
const SwingingHero = ({ hero, onDone }) => {
  useEffect(() => {
    playWhoosh();
    const t = setTimeout(() => { onDone && onDone(); }, 2400);
    return () => clearTimeout(t);
  }, [onDone]);

  const isMiles = hero === "miles";
  // Palette per Miles/Peter — Miles gets deep black suit + red accents
  // + a bit of electric-cyan spark; Peter is classic royal-blue + red.
  const suitPrimary  = isMiles ? "#0F0F14" : "#B91C1C"; // deep midnight vs crimson
  const suitSecondary = isMiles ? "#B91C1C" : "#1E40AF"; // red accents vs royal blue
  const eyeStroke = "#000000";
  const emblemColor = "#000000";
  const webLineColor = "#000000";

  return (
    <div className="fixed inset-0 z-[70] pointer-events-none overflow-hidden" data-testid="spider-swing">
      <div className="spider-swing-track">
        {/* Web strand from top-right */}
        <svg
          className="absolute -top-4 -right-2 w-24 h-64"
          viewBox="0 0 40 240"
          aria-hidden="true"
        >
          <line x1="20" y1="0" x2="20" y2="240" stroke="#ffffff" strokeWidth="2" opacity="0.92" />
        </svg>
        {/* Swinging hero — matches the WelcomeSplash art style
            (user report 2026-02: "the login Spider-Man looks cooler").
            Same clean silhouette: red head w/ white teardrop eyes,
            blue torso w/ black spider dot + web-line pattern, legs
            kicked out for the swing pose. */}
        <svg viewBox="0 0 120 220" className="w-40 h-64 sm:w-52 sm:h-80 drop-shadow-2xl" aria-hidden="true">
          {/* Web-shooter arm reaching up to the strand */}
          <path d="M 78 58 Q 90 30 100 6" stroke={suitPrimary} strokeWidth="10" fill="none" strokeLinecap="round" />

          {/* Head — classic Peter Parker mask: rounded forehead
              tapering into a defined chin, black web pattern radiating
              from the crown, and iconic pointed teardrop white lenses
              outlined in black. (User ask 2026-07-31: "I don't like the
              head.") */}
          <path
            d="M 60 26
               C 38 26, 30 42, 32 60
               C 34 78, 44 90, 60 92
               C 76 90, 86 78, 88 60
               C 90 42, 82 26, 60 26 Z"
            fill={suitPrimary}
          />
          {/* Web pattern — 8 radial strands from crown center + three
              concentric arcs, the signature Ditko mask detail */}
          <g stroke="#000" strokeWidth="0.8" fill="none" opacity="0.9" strokeLinecap="round">
            <line x1="60" y1="42" x2="32" y2="34" />
            <line x1="60" y1="42" x2="32" y2="52" />
            <line x1="60" y1="42" x2="34" y2="72" />
            <line x1="60" y1="42" x2="46" y2="90" />
            <line x1="60" y1="42" x2="60" y2="92" />
            <line x1="60" y1="42" x2="74" y2="90" />
            <line x1="60" y1="42" x2="86" y2="72" />
            <line x1="60" y1="42" x2="88" y2="52" />
            <line x1="60" y1="42" x2="88" y2="34" />
            <path d="M 36 40 Q 60 50 84 40" />
            <path d="M 33 62 Q 60 74 87 62" />
            <path d="M 38 82 Q 60 90 82 82" />
          </g>
          {/* Pointed teardrop eye lenses — angled inward, wider on
              the outer edge, tapering toward the nose. The look that
              makes it read as Spider-Man not "guy in a red hood". */}
          <path
            d="M 34 54
               Q 34 44 46 44
               Q 55 46 56 56
               Q 52 62 40 62
               Q 34 60 34 54 Z"
            fill="#FFFFFF" stroke="#000" strokeWidth="1.6"
          />
          <path
            d="M 86 54
               Q 86 44 74 44
               Q 65 46 64 56
               Q 68 62 80 62
               Q 86 60 86 54 Z"
            fill="#FFFFFF" stroke="#000" strokeWidth="1.6"
          />

          {/* Torso — blue tapered vest, matches splash geometry */}
          <path d="M 36 86 Q 60 96 84 86 L 88 156 Q 60 168 32 156 Z" fill={suitSecondary} />
          {/* Web-line pattern on torso — under the emblem so the
              spider reads on top */}
          <g stroke={webLineColor} strokeWidth="0.9" opacity="0.4" fill="none">
            <line x1="38" y1="104" x2="82" y2="104" />
            <line x1="36" y1="118" x2="84" y2="118" />
            <line x1="34" y1="134" x2="86" y2="134" />
            <line x1="34" y1="150" x2="86" y2="150" />
            <line x1="60" y1="90" x2="60" y2="162" />
            <line x1="46" y1="92" x2="50" y2="160" />
            <line x1="74" y1="92" x2="70" y2="160" />
          </g>
          {/* Chest spider emblem — proper 8-legged silhouette
              (user report 2026-07-31: "he doesn't even have legs
              on his shirt just a dot"). Head + abdomen body, four
              legs each side. */}
          <g fill={emblemColor}>
            <ellipse cx="60" cy="130" rx="8" ry="10" />
            <ellipse cx="60" cy="118" rx="4.5" ry="5.5" />
          </g>
          <g stroke={emblemColor} strokeWidth="2.4" fill="none" strokeLinecap="round">
            {/* Left legs */}
            <path d="M 54 122 Q 42 116 34 110" />
            <path d="M 53 128 Q 40 128 32 128" />
            <path d="M 53 134 Q 40 140 34 148" />
            <path d="M 55 140 Q 48 152 44 162" />
            {/* Right legs */}
            <path d="M 66 122 Q 78 116 86 110" />
            <path d="M 67 128 Q 80 128 88 128" />
            <path d="M 67 134 Q 80 140 86 148" />
            <path d="M 65 140 Q 72 152 76 162" />
          </g>

          {/* Legs kicked out for the swing */}
          <path d="M 40 156 Q 28 180 20 208" stroke={suitSecondary} strokeWidth="14" fill="none" strokeLinecap="round" />
          <path d="M 80 156 Q 94 178 104 200" stroke={suitSecondary} strokeWidth="14" fill="none" strokeLinecap="round" />

          {/* Free arm flared for balance */}
          <path d="M 36 96 Q 18 108 10 128" stroke={suitPrimary} strokeWidth="11" fill="none" strokeLinecap="round" />

          {/* Miles electric spark accent */}
          {isMiles && (
            <g stroke="#38BDF8" strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.9">
              <path d="M 10 128 L 4 134 L 10 138" />
              <path d="M 100 6 L 96 0 L 100 -3" />
            </g>
          )}
        </svg>
      </div>
    </div>
  );
};

/** Confetti of tiny web spinners — one-shot burst. */
const WebConfetti = ({ onDone }) => {
  useEffect(() => {
    const t = setTimeout(() => onDone && onDone(), 2200);
    return () => clearTimeout(t);
  }, [onDone]);
  const items = useMemo(() => {
    return Array.from({ length: 24 }).map((_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 0.4,
      duration: 1.4 + Math.random() * 0.8,
      size: 14 + Math.random() * 16,
      spin: (Math.random() * 720 - 360) | 0,
    }));
  }, []);
  return (
    <div className="fixed inset-0 z-[70] pointer-events-none overflow-hidden" data-testid="web-confetti">
      {items.map((it) => (
        <span
          key={it.id}
          className="absolute web-confetti-piece text-white"
          style={{
            left: `${it.left}%`,
            top: "-30px",
            fontSize: `${it.size}px`,
            animationDelay: `${it.delay}s`,
            animationDuration: `${it.duration}s`,
            "--spin": `${it.spin}deg`,
          }}
          aria-hidden="true"
        >
          ✳
        </span>
      ))}
    </div>
  );
};

export default function SpiderManTheme({ active }) {
  const [hero] = useState(pickHero);
  // Trigger sources — set by App.js via a small event bus (window
  // events). We listen here so the component can be mounted once
  // globally and pages just dispatch "spider:swing" / "spider:thwip".
  const [swingCount, setSwingCount] = useState(0);
  const [confettiCount, setConfettiCount] = useState(0);
  const [heroMode, setHeroMode] = useState(false);

  useEffect(() => {
    if (!active) return;
    const onSwing = () => setSwingCount((n) => n + 1);
    const onConfetti = () => setConfettiCount((n) => n + 1);
    const onThwip = () => { playThwip(); setConfettiCount((n) => n + 1); };
    const onPow = () => { playPow(); setSwingCount((n) => n + 1); };
    const onHero = () => {
      setHeroMode(true);
      playWhoosh();
      setTimeout(() => setHeroMode(false), 6000);
    };
    window.addEventListener("spider:swing", onSwing);
    window.addEventListener("spider:thwip", onThwip);
    window.addEventListener("spider:confetti", onConfetti);
    window.addEventListener("spider:pow", onPow);
    window.addEventListener("spider:hero", onHero);
    return () => {
      window.removeEventListener("spider:swing", onSwing);
      window.removeEventListener("spider:thwip", onThwip);
      window.removeEventListener("spider:confetti", onConfetti);
      window.removeEventListener("spider:pow", onPow);
      window.removeEventListener("spider:hero", onHero);
    };
  }, [active]);

  // Welcome swing — one-shot animation the FIRST time the theme is
  // activated in a browser session, plus a periodic random swing every
  // 60–120s while the theme stays on so Spider-Man isn't a ghost.
  // (User report 2026-02: "I don't see Spider-Man anywhere.")
  // The session-scoped flag prevents re-firing on every page nav.
  useEffect(() => {
    if (!active) return;
    let welcomeTimer, loopTimer;
    let cancelled = false;
    const alreadyGreeted = (() => {
      try { return sessionStorage.getItem("spider:greeted") === "1"; }
      catch { return false; }
    })();
    if (!alreadyGreeted) {
      welcomeTimer = setTimeout(() => {
        if (cancelled) return;
        try { sessionStorage.setItem("spider:greeted", "1"); } catch { /* ignore */ }
        setSwingCount((n) => n + 1);
      }, 900);
    }
    // Periodic swing loop — random 20–45s cadence keeps it lively
    // (was 60-120s but the user reported "nothing is happening").
    // Skipped when the tab is hidden so a backgrounded tab doesn't
    // pile up whooshes when it wakes.
    const scheduleNext = () => {
      const delay = 20000 + Math.random() * 25000;
      loopTimer = setTimeout(() => {
        if (cancelled) return;
        if (document.visibilityState === "visible") {
          setSwingCount((n) => n + 1);
        }
        scheduleNext();
      }, delay);
    };
    scheduleNext();
    return () => {
      cancelled = true;
      clearTimeout(welcomeTimer);
      clearTimeout(loopTimer);
    };
  }, [active]);

  // Prime the audio context on the first user tap after the theme
  // activates. Some iOS Safari builds keep the AudioContext suspended
  // until a gesture is registered.
  useEffect(() => {
    if (!active) return;
    const prime = () => { unlockAudio(); };
    window.addEventListener("pointerdown", prime, { once: true });
    return () => window.removeEventListener("pointerdown", prime);
  }, [active]);

  if (!active) return null;

  return (
    <>
      {/* Global CSS lives here so we don't pollute the app's stylesheet
          when the theme is off. Keyframes drive the swing across the
          viewport + the confetti fall. */}
      <style>{`
        @keyframes spider-swing-across {
          0%   { transform: translate(-15vw, -20vh) rotate(-12deg); opacity: 0; }
          10%  { opacity: 1; }
          50%  { transform: translate(45vw, 20vh) rotate(-2deg); }
          90%  { opacity: 1; }
          100% { transform: translate(115vw, -10vh) rotate(8deg); opacity: 0; }
        }
        .spider-swing-track {
          position: absolute;
          top: 0;
          left: 0;
          animation: spider-swing-across 2.3s cubic-bezier(.22,.61,.36,1) forwards;
          will-change: transform, opacity;
        }
        @keyframes web-confetti-fall {
          0%   { transform: translateY(0) rotate(0); opacity: 0; }
          15%  { opacity: 1; }
          100% { transform: translateY(110vh) rotate(var(--spin, 360deg)); opacity: 0; }
        }
        .web-confetti-piece {
          animation-name: web-confetti-fall;
          animation-timing-function: cubic-bezier(.36,.02,.66,.99);
          animation-fill-mode: forwards;
          text-shadow: 0 1px 2px rgba(0,0,0,0.35);
        }
        /* Hero mode — subtle red pulse on the app frame. */
        .spider-hero-mode::before {
          content: "";
          position: fixed;
          inset: 0;
          pointer-events: none;
          z-index: 9;
          box-shadow: inset 0 0 120px 20px rgba(220, 38, 38, 0.45);
          animation: spider-hero-pulse 1.5s ease-in-out infinite alternate;
        }
        @keyframes spider-hero-pulse {
          from { opacity: 0.5; }
          to   { opacity: 1; }
        }
        /* Web corners — pinned to the four corners of the viewport at
           low opacity so they don't fight the app content. */
        .spider-corner {
          position: fixed;
          pointer-events: none;
          z-index: 5;
          filter: drop-shadow(0 1px 3px rgba(0,0,0,0.15));
        }
        .spider-corner.tl { top: 0; left: 0; }
        .spider-corner.tr { top: 0; right: 0; }
        .spider-corner.bl { bottom: 0; left: 0; transform: scaleY(-1); }
        .spider-corner.br { bottom: 0; right: 0; transform: scale(-1, -1); }
        /* Hanging spider bob — subtle up-and-down oscillation so the
           mascot feels like he's swaying on his web strand. */
        @keyframes spider-mascot-bob {
          0%, 100% { transform: translateY(0) rotate(-2deg); }
          50%      { transform: translateY(6px) rotate(2deg); }
        }
        .spider-mascot-bob {
          animation: spider-mascot-bob 3.2s ease-in-out infinite;
        }
        .spider-mascot-bob:hover {
          animation-play-state: paused;
          transform: scale(1.15);
          transition: transform 0.15s ease-out;
        }
        /* Attention-grabber for the mascot hint pill — quick pulse
           so it stands out for the first few seconds a manager sees
           the Spider-Man theme. */
        @keyframes spider-hint-flash {
          0%, 100% { transform: scale(1); opacity: 1; }
          50%      { transform: scale(1.08); opacity: 0.85; }
        }
        .spider-hint-flash {
          animation: spider-hint-flash 1.2s ease-in-out infinite;
        }
        /* Parallax NYC skyline — a thin decorative strip pinned to the
           bottom of the viewport. Kept intentionally low + faint so
           it never bleeds through into transparent pages (Zone Sheet,
           Collect Email). Slow horizontal drift creates the parallax
           feel. Skipped for prefers-reduced-motion. */
        .spider-city {
          position: fixed;
          left: 0; right: 0; bottom: 0;
          pointer-events: none;
          z-index: 0;
          overflow: hidden;
          height: 22vh;
          max-height: 200px;
          /* Mask to fade the top edge into transparency so it feels
             like a distant horizon, not a wall */
          -webkit-mask-image: linear-gradient(to top, black 55%, transparent 100%);
                  mask-image: linear-gradient(to top, black 55%, transparent 100%);
        }
        .spider-city .city-layer {
          position: absolute;
          left: -20%;
          bottom: 0;
          width: 240%;
          height: 100%;
          will-change: transform;
        }
        .spider-city .city-far {
          opacity: 0.14;
          animation: spider-city-drift 90s linear infinite;
        }
        .spider-city .city-near {
          opacity: 0.22;
          animation: spider-city-drift 55s linear infinite;
        }
        .spider-city .city-glow {
          position: absolute;
          inset: 0;
          background: linear-gradient(to top,
            rgba(220, 38, 38, 0.10) 0%,
            transparent 70%);
        }
        @keyframes spider-city-drift {
          from { transform: translateX(0); }
          to   { transform: translateX(-25%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .spider-city .city-far,
          .spider-city .city-near { animation: none; }
        }
      `}</style>

      {/* Parallax NYC-style skyline pinned to the viewport bottom.
          Two layers drift at different speeds for depth. Sits behind
          all app content (z-index 0) at low opacity — a subtle "we're
          swinging over the city" cue without stealing focus from the
          dashboard. */}
      <div className="spider-city" aria-hidden="true" data-testid="spider-city">
        <div className="city-glow" />
        {/* Far layer — smaller, dimmer skyscrapers */}
        <svg className="city-layer city-far" viewBox="0 0 2400 340" preserveAspectRatio="xMinYEnd meet">
          <g fill="#0B1220">
            {/* Repeatable skyline block A (0-1200) */}
            <rect x="0"    y="180" width="90"  height="160" />
            <rect x="95"   y="140" width="70"  height="200" />
            <rect x="170"  y="200" width="60"  height="140" />
            <rect x="235"  y="120" width="110" height="220" />
            <polygon points="290,120 345,120 317,80" />
            <rect x="350"  y="170" width="80"  height="170" />
            <rect x="435"  y="150" width="55"  height="190" />
            <rect x="495"  y="190" width="95"  height="150" />
            <rect x="595"  y="130" width="85"  height="210" />
            <rect x="685"  y="180" width="70"  height="160" />
            <rect x="760"  y="160" width="120" height="180" />
            <polygon points="820,160 880,160 850,110" />
            <rect x="885"  y="200" width="60"  height="140" />
            <rect x="950"  y="140" width="90"  height="200" />
            <rect x="1045" y="185" width="75"  height="155" />
            <rect x="1125" y="165" width="70"  height="175" />
            {/* Block B — clone (1200-2400) */}
            <rect x="1200" y="180" width="90"  height="160" />
            <rect x="1295" y="140" width="70"  height="200" />
            <rect x="1370" y="200" width="60"  height="140" />
            <rect x="1435" y="120" width="110" height="220" />
            <polygon points="1490,120 1545,120 1517,80" />
            <rect x="1550" y="170" width="80"  height="170" />
            <rect x="1635" y="150" width="55"  height="190" />
            <rect x="1695" y="190" width="95"  height="150" />
            <rect x="1795" y="130" width="85"  height="210" />
            <rect x="1885" y="180" width="70"  height="160" />
            <rect x="1960" y="160" width="120" height="180" />
            <polygon points="2020,160 2080,160 2050,110" />
            <rect x="2085" y="200" width="60"  height="140" />
            <rect x="2150" y="140" width="90"  height="200" />
            <rect x="2245" y="185" width="75"  height="155" />
            <rect x="2325" y="165" width="70"  height="175" />
          </g>
          {/* Faint lit windows */}
          <g fill="#FBBF24" opacity="0.55">
            {Array.from({ length: 60 }).map((_, i) => {
              const x = (i * 41) % 2400;
              const y = 200 + ((i * 7) % 100);
              return <rect key={i} x={x} y={y} width="2.5" height="4" />;
            })}
          </g>
        </svg>
        {/* Near layer — taller, sharper, more defined buildings */}
        <svg className="city-layer city-near" viewBox="0 0 2400 340" preserveAspectRatio="xMinYEnd meet">
          <g fill="#04070E">
            {/* Repeatable skyline block A (0-1200) */}
            <rect x="0"    y="110" width="120" height="230" />
            <rect x="125"  y="60"  width="90"  height="280" />
            <polygon points="140,60 200,60 170,20" />
            <rect x="220"  y="150" width="100" height="190" />
            <rect x="325"  y="90"  width="140" height="250" />
            <rect x="470"  y="180" width="80"  height="160" />
            <rect x="555"  y="120" width="110" height="220" />
            <rect x="670"  y="70"  width="70"  height="270" />
            <polygon points="670,70 740,70 705,30" />
            <rect x="745"  y="160" width="95"  height="180" />
            <rect x="845"  y="100" width="130" height="240" />
            <rect x="980"  y="140" width="85"  height="200" />
            <rect x="1070" y="80"  width="105" height="260" />
            <polygon points="1070,80 1175,80 1122,40" />
            {/* Block B — clone (1200-2400) */}
            <rect x="1200" y="110" width="120" height="230" />
            <rect x="1325" y="60"  width="90"  height="280" />
            <polygon points="1340,60 1400,60 1370,20" />
            <rect x="1420" y="150" width="100" height="190" />
            <rect x="1525" y="90"  width="140" height="250" />
            <rect x="1670" y="180" width="80"  height="160" />
            <rect x="1755" y="120" width="110" height="220" />
            <rect x="1870" y="70"  width="70"  height="270" />
            <polygon points="1870,70 1940,70 1905,30" />
            <rect x="1945" y="160" width="95"  height="180" />
            <rect x="2045" y="100" width="130" height="240" />
            <rect x="2180" y="140" width="85"  height="200" />
            <rect x="2270" y="80"  width="105" height="260" />
            <polygon points="2270,80 2375,80 2322,40" />
          </g>
          {/* Lit windows — warm amber, tighter grid */}
          <g fill="#FDE68A" opacity="0.72">
            {Array.from({ length: 100 }).map((_, i) => {
              const x = (i * 29) % 2400;
              const y = 130 + ((i * 11) % 180);
              return <rect key={i} x={x} y={y} width="3" height="5" />;
            })}
          </g>
          {/* A few red radio-tower blinks — pure decoration */}
          <g fill="#DC2626">
            <circle cx="170"  cy="18"  r="3" />
            <circle cx="1122" cy="38"  r="3" />
            <circle cx="1370" cy="18"  r="3" />
            <circle cx="2322" cy="38"  r="3" />
          </g>
        </svg>
      </div>

      {/* Static web corners on every page */}
      <div className="spider-corner tl"><WEB_CORNER color="#dc2626" opacity={0.32} /></div>
      <div className="spider-corner tr"><WEB_CORNER flip color="#dc2626" opacity={0.32} /></div>
      <div className="spider-corner bl"><WEB_CORNER color="#dc2626" opacity={0.2} /></div>
      <div className="spider-corner br"><WEB_CORNER flip color="#dc2626" opacity={0.2} /></div>

      {/* Hanging spider mascot — pinned to the bottom-right corner
          on mobile + desktop so it never overlaps the profile avatar
          (which sits top-right on the header). Bobs gently, tap to
          fire a fresh swing. (User report 2026-02: mascot was
          blocking taps on the profile picture.) */}
      <div className="fixed bottom-4 right-4 z-[8] flex items-center gap-2" style={{ pointerEvents: "auto" }}>
        <span
          className="hidden sm:inline-block px-2 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-red-600 text-white shadow-lg spider-hint-flash"
          data-testid="spider-mascot-hint"
        >
          Tap me! →
        </span>
        <button
          type="button"
          aria-label="Say hi to Spider-Man"
          onClick={() => {
            playWhoosh();
            setSwingCount((n) => n + 1);
          }}
          className="w-14 h-14 flex items-center justify-center rounded-full bg-transparent border-none cursor-pointer spider-mascot-bob"
          data-testid="spider-mascot"
        >
        {/* Mini hanging-spider SVG. Web strand extends up out of the
            viewport so it looks like he's dangling from off-screen. */}
        <svg viewBox="0 0 60 90" className="w-full h-full drop-shadow-md" aria-hidden="true">
          <line x1="30" y1="0" x2="30" y2="30" stroke="#ffffff" strokeWidth="1.2" opacity="0.7" />
          {/* Body */}
          <ellipse cx="30" cy="45" rx="14" ry="16" fill={hero === "miles" ? "#111827" : "#1e40af"} />
          <ellipse cx="30" cy="42" rx="11" ry="12" fill="#dc2626" />
          {/* Eyes */}
          <ellipse cx="26" cy="41" rx="3.5" ry="4.5" fill="#ffffff" />
          <ellipse cx="34" cy="41" rx="3.5" ry="4.5" fill="#ffffff" />
          {/* Web strand pattern */}
          <g stroke="#000" strokeWidth="0.5" opacity="0.35" fill="none">
            <line x1="20" y1="42" x2="40" y2="42" />
            <line x1="30" y1="34" x2="30" y2="60" />
          </g>
          {/* 8 dangling spider legs */}
          <g stroke={hero === "miles" ? "#111827" : "#1e40af"} strokeWidth="2" fill="none" strokeLinecap="round">
            <path d="M 20 50 Q 12 58 8 68" />
            <path d="M 22 55 Q 14 65 10 78" />
            <path d="M 24 58 Q 18 70 16 82" />
            <path d="M 27 60 Q 24 72 22 85" />
            <path d="M 33 60 Q 36 72 38 85" />
            <path d="M 36 58 Q 42 70 44 82" />
            <path d="M 38 55 Q 46 65 50 78" />
            <path d="M 40 50 Q 48 58 52 68" />
          </g>
        </svg>
      </button>
      </div>

      {/* Hero mode wrapper — applied via class on body */}
      {heroMode && <div className="spider-hero-mode" aria-hidden="true" />}

      {/* One-shot animations — remounted per event via key */}
      {swingCount > 0 && (
        <SwingingHero
          key={`swing-${swingCount}`}
          hero={hero}
          onDone={() => { /* keep counter — no reset needed */ }}
        />
      )}
      {confettiCount > 0 && (
        <WebConfetti
          key={`conf-${confettiCount}`}
          onDone={() => { /* same — remount by counter */ }}
        />
      )}
    </>
  );
}
